import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, BookOpen } from '../lib/icons';
import { useFeaturedBlogPosts } from '../hooks/useBlog';
import { createUniqueSlug } from '../utils/slugUtils';
import BlogCard from './BlogCard';

const BlogSection: React.FC = () => {
  // Lädt nur die 6 neuesten Posts ohne full content (Listen-Felder reichen
  // fuer die Card-Anzeige). Vorher useBlogPosts() — alle 228 Posts inkl.
  // HTML-Content, ~1 MB Payload pro Home-Visit.
  const { posts: featuredPosts, loading, error } = useFeaturedBlogPosts(6);
  const navigate = useNavigate();

  const handleBlogPostClick = (postId: string) => {
    const post = featuredPosts.find(p => p.id === postId);
    if (post) {
      const slug = createUniqueSlug(post.title, post.id);
      navigate(`/wissen/${slug}`);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Artikel...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600">Fehler beim Laden der Artikel: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (featuredPosts.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="p-4">
                {/* Titel als prominentestes Element */}
                <h2 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight">
                  Wissen für Ihre Gesundheit
                </h2>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                  Bald finden Sie hier aktuelle Gesundheitstipps und medizinische Informationen
                </p>
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Wissen für Ihre Gesundheit
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Aktuelle Gesundheitstipps und medizinische Informationen von unseren Experten
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredPosts.map((post) => (
            <BlogCard
              key={post.id}
              post={post}
              onClick={handleBlogPostClick}
            />
          ))}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={() => navigate('/wissen')}
            className="bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Alle Artikel anzeigen
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;